import { useCallback, useEffect, useRef, useState } from 'react';
import { isTossApp } from '../utils/env';

export type TossAppLoginResult = {
  authorizationCode: string;
  referrer: string;
};

export type TossAppLoginState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: TossAppLoginResult }
  | { status: 'error'; message: string };

const ERROR_MESSAGE = '토스 로그인에 실패했습니다. 다시 시도해 주세요.';

/**
 * Toss app SDK의 appLogin을 호출해 authorizationCode + referrer를 받는 순수 함수.
 * 훅 외부에서도 단위 테스트 가능하도록 분리.
 */
export async function runTossAppLogin(): Promise<TossAppLoginResult> {
  const sdk = await import('@apps-in-toss/web-framework');
  const { authorizationCode, referrer } = await sdk.appLogin();
  return { authorizationCode, referrer };
}

export interface UseTossAppLoginOptions {
  autoStart?: boolean;
}

export interface UseTossAppLoginReturn {
  state: TossAppLoginState;
  trigger: () => Promise<void>;
  reset: () => void;
}

export function useTossAppLogin(
  options: UseTossAppLoginOptions = {}
): UseTossAppLoginReturn {
  const { autoStart = false } = options;
  const [state, setState] = useState<TossAppLoginState>({ status: 'idle' });

  // 동시 호출 가드
  const inFlightRef = useRef(false);

  const trigger = useCallback(async () => {
    if (!isTossApp()) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setState({ status: 'loading' });
    try {
      const data = await runTossAppLogin();
      setState({ status: 'success', data });
    } catch {
      setState({ status: 'error', message: ERROR_MESSAGE });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle' });
  }, []);

  useEffect(() => {
    if (autoStart && isTossApp()) {
      void trigger();
    }
    // trigger / autoStart는 마운트 시 1회만 동작. 의존성 의도적으로 비움.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, trigger, reset };
}
